import { lineupGaps, parseLineupText, type ParsedRow } from "@festipal/shared";
import { useMemo, useState } from "react";

import { ApiError, festivals } from "./api";

/**
 * Import d'une programmation collée telle quelle.
 *
 * Complète l'import CSV, qui suppose des colonnes : une programmation se trouve
 * le plus souvent en blocs de texte, sur un site ou dans un PDF dont on copie le
 * contenu.
 *
 * L'analyse a lieu dans le navigateur, à chaque frappe : coller, voir, corriger.
 * Rien ne part au serveur avant que la grille soit relue — c'est la file de
 * validation humaine de CONTENU.md §1, ramenée à un écran.
 */

type Props = {
  slug: string;
  onImported: () => void;
};

/** Échappement CSV : un nom d'artiste peut contenir un point-virgule. */
function csvCell(value: string): string {
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function LineupPaste({ slug, onImported }: Props) {
  const [text, setText] = useState("");
  const [rows, setRows] = useState<ParsedRow[] | null>(null);
  const [unknown, setUnknown] = useState<{ line: number; text: string }[]>([]);
  const [dates, setDates] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");

  /**
   * Rapatrie le texte d'une page.
   *
   * Il atterrit dans le même champ que le collage : la suite est identique, et
   * il n'y a qu'un seul chemin de relecture à comprendre — comme à maintenir.
   */
  const fetchFromUrl = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await festivals.fetchSource(slug, url.trim());
      setText(result.text);
      setMessage("Page récupérée. Relisez le texte, puis analysez-le.");
    } catch (cause) {
      setMessage(
        cause instanceof ApiError && cause.status === 422
          ? "Cette page n'a pas pu être récupérée. Copiez-collez son contenu à la place."
          : "Récupération impossible.",
      );
    } finally {
      setBusy(false);
    }
  };

  /**
   * Extrait le texte d'un PDF.
   *
   * Il atterrit dans le même champ que le collage et que la page récupérée : un
   * seul chemin de relecture, quelle que soit la provenance.
   */
  const readPdf = async (file: File) => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await festivals.importPdf(slug, file);
      setText(result.text);
      setMessage(
        `${result.pages} page(s) lue(s). Relisez le texte — un PDF se lit rarement parfaitement — puis analysez-le.`,
      );
    } catch (cause) {
      setMessage(
        cause instanceof ApiError && cause.status === 422
          ? `Extraction impossible : ${cause.code}`
          : "Lecture du PDF impossible.",
      );
    } finally {
      setBusy(false);
    }
  };

  const analyse = () => {
    const parsed = parseLineupText(text);
    setRows(parsed.rows);
    setUnknown(parsed.unknown);
    setMessage(lineupGaps(parsed).join(" ") || null);
  };

  /** Intitulés de jour rencontrés, dans l'ordre d'apparition. */
  const dayLabels = useMemo(
    () => [...new Set((rows ?? []).map((row) => row.day).filter(Boolean))],
    [rows],
  );

  const update = (index: number, field: keyof ParsedRow, value: string) => {
    setRows((current) =>
      (current ?? []).map((row, position) =>
        position === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const missingDate = dayLabels.some((label) => !dates[label]);
  const canImport = (rows?.length ?? 0) > 0 && !missingDate && !busy;

  const confirm = async () => {
    if (!rows) return;
    setBusy(true);
    setMessage(null);

    // La grille repasse par l'import existant : une seule route d'entrée, donc
    // une seule série de contrôles côté serveur.
    const content = [
      "Scène;Artiste;Jour;Début;Fin",
      ...rows.map((row) =>
        [
          csvCell(row.stage),
          csvCell(row.artist),
          dates[row.day] ?? "",
          row.start,
          row.end ?? "",
        ].join(";"),
      ),
    ].join("\n");

    try {
      const result = await festivals.importLineup(slug, { content, dryRun: false });
      setMessage(`${result.created} concert(s) importé(s). Publiez pour les diffuser.`);
      setRows(null);
      setText("");
      onImported();
    } catch {
      setMessage("Import impossible.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <h2>Coller une programmation</h2>
      <p className="muted">
        Donnez l'adresse de la page, ou copiez le programme depuis un site ou un PDF et
        collez-le ici, tel quel. Les jours, les scènes et les horaires sont reconnus ;
        vous corrigez ce qui a été mal lu avant d'importer.
      </p>

      <label>
        Fichier PDF (facultatif)
        <input
          type="file"
          accept="application/pdf,.pdf"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void readPdf(file);
          }}
        />
      </label>

      <label>
        Adresse de la page (facultatif)
        <input
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://exemple.fr/programmation"
        />
      </label>
      <div className="row">
        <button onClick={() => void fetchFromUrl()} disabled={busy || !url.trim()}>
          Récupérer la page
        </button>
        <span className="muted">
          Le texte revient ici pour relecture ; rien n'est enregistré à ce stade.
        </span>
      </div>

      <label>
        Programme
        <textarea
          value={text}
          onChange={(event) => setText(event.target.value)}
          rows={8}
          placeholder={
            "VENDREDI 4 AOÛT\nGrande scène\n18h30 Artiste A\n20h00 – 21h30 Artiste B"
          }
        />
      </label>

      <div className="row">
        <button className="primary" onClick={analyse} disabled={!text.trim()}>
          Analyser
        </button>
      </div>

      {message ? <p className="muted">{message}</p> : null}

      {unknown.length > 0 ? (
        <div className="error">
          <strong>Lignes non comprises</strong>
          <ul>
            {unknown.map((entry) => (
              <li key={entry.line}>
                Ligne {entry.line} : {entry.text}
              </li>
            ))}
          </ul>
          {/* Rendues plutôt qu'écartées : c'est la seule façon de savoir ce qui
              manque avant de découvrir le trou pendant le festival. */}
        </div>
      ) : null}

      {dayLabels.length > 0 ? (
        <>
          <h3 style={{ margin: 0, fontSize: "1rem" }}>Dates des journées</h3>
          <p className="muted">
            Une affiche donne rarement l'année : indiquez la date de chaque journée
            reconnue.
          </p>
          {dayLabels.map((label) => (
            <label key={label} className="ink-row">
              <span>{label}</span>
              <input
                type="date"
                value={dates[label] ?? ""}
                onChange={(event) =>
                  setDates((current) => ({ ...current, [label]: event.target.value }))
                }
                aria-label={`Date pour ${label}`}
              />
            </label>
          ))}
        </>
      ) : null}

      {rows && rows.length > 0 ? (
        <>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Ligne</th>
                  <th>Scène</th>
                  <th>Artiste</th>
                  <th>Début</th>
                  <th>Fin</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.line}-${index}`}>
                    <td className="muted">{row.line}</td>
                    <td>
                      <input
                        value={row.stage}
                        onChange={(event) => update(index, "stage", event.target.value)}
                        aria-label={`Scène, ligne ${row.line}`}
                      />
                    </td>
                    <td>
                      <input
                        value={row.artist}
                        onChange={(event) =>
                          update(index, "artist", event.target.value)
                        }
                        aria-label={`Artiste, ligne ${row.line}`}
                      />
                    </td>
                    <td>
                      <input
                        value={row.start}
                        onChange={(event) => update(index, "start", event.target.value)}
                        aria-label={`Début, ligne ${row.line}`}
                        style={{ width: 80 }}
                      />
                    </td>
                    <td>
                      <input
                        value={row.end ?? ""}
                        onChange={(event) => update(index, "end", event.target.value)}
                        aria-label={`Fin, ligne ${row.line}`}
                        style={{ width: 80 }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row">
            <button
              className="primary"
              onClick={() => void confirm()}
              disabled={!canImport}
            >
              Importer {rows.length} concert(s)
            </button>
            {missingDate ? (
              <span className="badge pending">Indiquez d'abord chaque date</span>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}
